import React, { useEffect, useState, useCallback } from "react";
import { Spin } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { favorites } from "../../store";
import { SearchBar, FavoriteIcon, useToast } from "../../components";
import "./favorites.css";

const Favorites = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState("TOPIC");

  const {
    topicFavorites,
    topicMap,
    topicLoading,
    coachFavorites,
    coachMap,
    coachLoading,
    _pendingAdd,
  } = useSelector((state) => state.favorites);

  // Fetch data based on active tab
  useEffect(() => {
    dispatch(favorites.fetchFavorites(activeTab));
  }, [dispatch, activeTab]);

  const handleToggleFavorite = useCallback(
    (item, type) => {
      const itemId = item.itemId;
      const map = type === "TOPIC" ? topicMap : coachMap;
      const favoriteDocId = map[itemId];

      if (favoriteDocId && favoriteDocId !== "pending") {
        dispatch(
          favorites.removeFavorite({ favoriteDocId, itemId, type })
        ).then((res) => {
          if (res.meta.requestStatus === "fulfilled") {
            toast.success("Removed from favorites");
          } else if (res.meta.requestStatus === "rejected") {
            toast.error("Failed to remove from favorites");
          }
        });
      } else {
        dispatch(favorites.addFavorite(itemId)).then((res) => {
          if (res.meta.requestStatus === "fulfilled") {
            toast.success("Added to favorites");
          } else if (res.meta.requestStatus === "rejected") {
            toast.error("Failed to add to favorites");
          }
        });
      }
    },
    [dispatch, topicMap, coachMap, toast]
  );

  const handleTopicClick = (topicId) => {
    navigate(`/topic/${topicId}`);
  };

  const isPending = (itemId, type) => {
    return _pendingAdd[itemId] === "pending";
  };

  const isFavorited = (itemId, type) => {
    const map = type === "TOPIC" ? topicMap : coachMap;
    return !!map[itemId] && map[itemId] !== "pending";
  };

  // Render topic cards
  const renderTopicCard = (item, index) => {
    const itemId = item.itemId;
    const isFeatured = index === 0;

    return (
      <div
        key={itemId}
        className={`favorites-card ${
          isFeatured ? "favorites-card--featured" : ""
        }`}
        onClick={() => handleTopicClick(itemId)}
      >
        {item.picture ? (
          <>
            <div
              className="favorites-card-bg"
              style={{ backgroundImage: `url(${item.picture})` }}
            />
            <div className="favorites-card-gradient" />
          </>
        ) : (
          <div className="favorites-card-gradient-only" />
        )}
        <div className="favorites-card-favorite">
          <FavoriteIcon
            isFavorited={isFavorited(itemId, "TOPIC")}
            loading={isPending(itemId, "TOPIC")}
            onToggle={() => handleToggleFavorite(item, "TOPIC")}
          />
        </div>
        <div className="favorites-card-content">
          <h3 className="favorites-card-title">
            {item.name || "Untitled Topic"}
          </h3>
          {isFeatured && item.description && (
            <p className="favorites-card-desc">{item.description}</p>
          )}
        </div>
      </div>
    );
  };

  // Render coach cards (same bento style as topic cards)
  const renderCoachCard = (item) => {
    const itemId = item.itemId;

    return (
      <div
        key={itemId}
        className="favorites-card"
        onClick={() => navigate(`/coach/${itemId}`)}
      >
        {item.picture ? (
          <>
            <div
              className="favorites-card-bg"
              style={{ backgroundImage: `url(${item.picture})` }}
            />
            <div className="favorites-card-gradient" />
          </>
        ) : (
          <div className="favorites-card-gradient-only" />
        )}
        <div className="favorites-card-favorite">
          <FavoriteIcon
            isFavorited={isFavorited(itemId, "COACH")}
            loading={isPending(itemId, "COACH")}
            onToggle={() => handleToggleFavorite(item, "COACH")}
          />
        </div>
        <div className="favorites-card-content">
          <h3 className="favorites-card-title">
            {item.name || "Unknown Coach"}
          </h3>
        </div>
      </div>
    );
  };

  // -------- Render --------

  const isLoading = activeTab === "TOPIC" ? topicLoading : coachLoading;
  const items = activeTab === "TOPIC" ? topicFavorites : coachFavorites;
  const isEmpty = !isLoading && items.length === 0;

  return (
    <div>
      <SearchBar />
      <div className="favorites-container">
        {/* Page Header */}
        <div className="favorites-header">
          <h1>Favorites</h1>
        </div>

        {/* Tabs */}
        <div
          className="favorites-tabs"
          style={{ paddingLeft: "16px", paddingRight: "16px" }}
        >
          <button
            className={`favorites-tab ${
              activeTab === "TOPIC" ? "favorites-tab--active" : ""
            }`}
            onClick={() => setActiveTab("TOPIC")}
          >
            Topics
          </button>
          <button
            className={`favorites-tab ${
              activeTab === "COACH" ? "favorites-tab--active" : ""
            }`}
            onClick={() => setActiveTab("COACH")}
          >
            Coaches
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="favorites-loading">
            <Spin size="large" />
          </div>
        )}

        {/* Empty State */}
        {isEmpty && (
          <div className="favorites-empty">
            <span className="material-symbols-outlined favorites-empty-icon">
              favorite
            </span>
            <h3 className="favorites-empty-title">
              {activeTab === "TOPIC"
                ? "No favorite topics yet"
                : "No favorite coaches yet"}
            </h3>
            <p className="favorites-empty-desc">
              {activeTab === "TOPIC"
                ? "Start exploring and save your favorite training topics to access them quickly."
                : "Start exploring and save your preferred coaches to access them quickly."}
            </p>
          </div>
        )}

        {/* Topics Grid */}
        {!isLoading && activeTab === "TOPIC" && items.length > 0 && (
          <div className="favorites-grid">
            {items.map((item, index) => renderTopicCard(item, index))}
          </div>
        )}

        {/* Coaches Grid */}
        {!isLoading && activeTab === "COACH" && items.length > 0 && (
          <div className="favorites-grid">
            {items.map((item) => renderCoachCard(item))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Favorites;
